import {
  UserPermissionsRequest,
  PermissionGroup,
  PermissionGroupCreateRequest,
  PermissionGroupUpdateRequest,
  Program,
  SuccessResponse,
  EffectivePermission,
  UserPermission,
  UserPermissionMatrix,
  PermissionAnalysis,
  GroupMembers,
  GroupPermissions,
  ProgramTree,
  PaginationMeta,
  V2RawPermission,
  CurrentUserEffectivePermission
} from '../types/user';
import apiClient from '../services/apiClient';

// Permission Management V2 API — all paths are explicit /api/v2/*
export const permissionApiV2 = {
  // === Permission Groups ===

  async getPermissionGroups(params?: {
    page?: number; limit?: number; search?: string; isActive?: boolean
  }): Promise<SuccessResponse<{ items: PermissionGroup[]; pagination: PaginationMeta }>> {
    const response = await apiClient.get<SuccessResponse<{ items: PermissionGroup[]; pagination: PaginationMeta }>>(
      '/api/v2/permission-groups', { params });
    return response.data;
  },

  async getPermissionGroup(groupId: string): Promise<SuccessResponse<PermissionGroup>> {
    const response = await apiClient.get<SuccessResponse<PermissionGroup>>(
      `/api/v2/permission-groups/${groupId}`);
    return response.data;
  },

  async createPermissionGroup(groupData: PermissionGroupCreateRequest): Promise<SuccessResponse<PermissionGroup>> {
    const response = await apiClient.post<SuccessResponse<PermissionGroup>>(
      '/api/v2/permission-groups', groupData);
    return response.data;
  },

  async updatePermissionGroup(groupId: string, groupData: PermissionGroupUpdateRequest): Promise<SuccessResponse<PermissionGroup>> {
    const response = await apiClient.put<SuccessResponse<PermissionGroup>>(
      `/api/v2/permission-groups/${groupId}`, groupData);
    return response.data;
  },

  async deletePermissionGroup(groupId: string): Promise<SuccessResponse<void>> {
    const response = await apiClient.delete<SuccessResponse<void>>(
      `/api/v2/permission-groups/${groupId}`);
    return response.data;
  },

  // === Group Members ===

  async getGroupMembers(groupId: string, params?: {
    page?: number; limit?: number; search?: string;
  }): Promise<SuccessResponse<GroupMembers>> {
    const response = await apiClient.get<SuccessResponse<GroupMembers>>(
      `/api/v2/permission-groups/${groupId}/members`, { params });
    return response.data;
  },

  async addGroupMembers(groupId: string, userIds: string[]): Promise<SuccessResponse<void>> {
    const response = await apiClient.post<SuccessResponse<void>>(
      `/api/v2/permission-groups/${groupId}/members`, { userIds });
    return response.data;
  },

  async removeGroupMembers(groupId: string, userIds: string[]): Promise<SuccessResponse<void>> {
    const response = await apiClient.delete<SuccessResponse<void>>(
      `/api/v2/permission-groups/${groupId}/members`, { data: { userIds } });
    return response.data;
  },

  // === Group Permissions ===

  async getGroupPermissions(groupId: string): Promise<SuccessResponse<GroupPermissions>> {
    const response = await apiClient.get<SuccessResponse<GroupPermissions>>(
      `/api/v2/permission-groups/${groupId}/permissions`);
    return response.data;
  },

  async setGroupPermissions(groupId: string, permissions: UserPermissionsRequest): Promise<SuccessResponse<void>> {
    const response = await apiClient.put<SuccessResponse<void>>(
      `/api/v2/permission-groups/${groupId}/permissions`, permissions);
    return response.data;
  },

  // === User Permissions ===

  async getUserPermissions(userId: string, params?: {
    page?: number; limit?: number;
  }): Promise<SuccessResponse<{ userId: string; userName: string; personalPermissions: V2RawPermission[] }>> {
    const response = await apiClient.get<SuccessResponse<{ userId: string; userName: string; personalPermissions: V2RawPermission[] }>>(
      `/api/v2/users/${userId}/permissions`, { params });
    return response.data;
  },

  async setUserPermissions(userId: string, permissions: UserPermissionsRequest): Promise<SuccessResponse<void>> {
    const response = await apiClient.put<SuccessResponse<void>>(
      `/api/v2/users/${userId}/permissions`, permissions);
    return response.data;
  },

  async getUserEffectivePermissions(userId: string): Promise<SuccessResponse<{
    userId: string; userName: string; effectivePermissions: EffectivePermission[];
  }>> {
    const response = await apiClient.get<SuccessResponse<{
      userId: string; userName: string; effectivePermissions: EffectivePermission[];
    }>>(`/api/v2/users/${userId}/effective-permissions`);
    return response.data;
  },

  async getCurrentUserPermissions(): Promise<SuccessResponse<{
    userId: string; userName: string; effectivePermissions: CurrentUserEffectivePermission[];
  }>> {
    const response = await apiClient.get<SuccessResponse<{
      userId: string; userName: string; effectivePermissions: CurrentUserEffectivePermission[];
    }>>('/api/v2/users/current/permissions');
    return response.data;
  },

  async getUserPermissionMatrix(userId: string): Promise<SuccessResponse<UserPermissionMatrix>> {
    const response = await apiClient.get<SuccessResponse<UserPermissionMatrix>>(
      `/api/v2/users/${userId}/permission-matrix`);
    return response.data;
  },

  // === Programs ===

  async getPrograms(params?: {
    page?: number; limit?: number; search?: string; isActive?: boolean;
  }): Promise<SuccessResponse<{ items: Program[]; pagination: PaginationMeta }>> {
    const response = await apiClient.get<SuccessResponse<{ items: Program[]; pagination: PaginationMeta }>>(
      '/api/v2/programs', { params });
    return response.data;
  },

  async getProgramTree(): Promise<SuccessResponse<ProgramTree[]>> {
    const response = await apiClient.get<SuccessResponse<ProgramTree[]>>('/api/v2/programs/tree');
    return response.data;
  },

  async getProgram(programId: number): Promise<SuccessResponse<Program>> {
    const response = await apiClient.get<SuccessResponse<Program>>(`/api/v2/programs/${programId}`);
    return response.data;
  },

  // === Permission Checks ===

  async checkPermission(userId: string, programId: number, permission: string): Promise<SuccessResponse<{
    hasPermission: boolean; userId: string; programId: number; permission: string;
  }>> {
    const response = await apiClient.get<SuccessResponse<{
      hasPermission: boolean; userId: string; programId: number; permission: string;
    }>>('/api/v2/permissions/check', { params: { userId, programId, permission } });
    return response.data;
  },

  async checkMultiplePermissions(checks: {
    userId: string;
    checks: Array<{ programId: number; permission: string }>;
  }): Promise<SuccessResponse<{
    results: Array<{ programId: number; permission: string; hasPermission: boolean }>;
  }>> {
    const response = await apiClient.post<SuccessResponse<{
      results: Array<{ programId: number; permission: string; hasPermission: boolean }>;
    }>>('/api/v2/permissions/check-multiple', checks);
    return response.data;
  },

  // === Permission Analysis ===

  async getUserPermissionAnalysis(userId: string): Promise<SuccessResponse<PermissionAnalysis>> {
    const response = await apiClient.get<SuccessResponse<PermissionAnalysis>>(
      `/api/v2/users/${userId}/permission-analysis`);
    return response.data;
  }
};

// Migration helper — format conversion between V2 response and frontend expectations
export const migrationHelper = {
  convertV1PermissionsToV2(v1Permissions: UserPermissionsRequest['permissions']): UserPermissionsRequest['permissions'] {
    return v1Permissions.map(perm => ({
      programId: perm.programId,
      addNew: perm.addNew ?? null,
      edit: perm.edit ?? null,
      delete: perm.delete ?? null,
      query: perm.query ?? null,
      export: perm.export ?? null,
      print: perm.print ?? null,
      approve: perm.approve ?? null
    }));
  },

  convertV2PermissionsToV1(v2Permissions: V2RawPermission[]): UserPermission[] {
    return v2Permissions.map(perm => ({
      programId: perm.programId ?? perm.ProgramId ?? 0,
      programName: perm.programName || perm.ProgramName || '',
      addNew: perm.addNew ?? perm.AddNew ?? null,
      edit: perm.edit ?? perm.Edit ?? null,
      delete: perm.delete ?? perm.Delete ?? perm.del ?? null,
      export: perm.export ?? perm.Export ?? null,
      print: perm.print ?? perm.Print ?? perm.printer ?? null,
      query: perm.query ?? perm.Query ?? null,
      approve: perm.approve ?? perm.Approve ?? null
    }));
  }
};

export default permissionApiV2;
